import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { WARootState } from '@/store/store';
import type {
  ModuleAccessResult,
  ModuleDefinition,
  UserRestriction,
} from '../types';

/**
 * Centralized hook for checking module, code, and feature permissions.
 *
 * Replaces duplicated validateByModule/validateByCode/validateByFeature logic
 * across the codebase with a single source of truth.
 *
 * @example
 * ```tsx
 * const { validateByModule, isModuleEnabled, getEnabledModules } = useModuleAccess();
 *
 * // Check if a module is enabled
 * if (validateByModule("econsent")) {
 *   // Show econsent button
 * }
 *
 * // Get all enabled modules from a definitions object
 * const enabledModules = getEnabledModules(quickLinkModuleDefinitions);
 * ```
 */
export const useModuleAccess = (): ModuleAccessResult => {
  const appUserInfo = useSelector((s: WARootState) => s.appView.UserInfo);

  const restrictions = useMemo<UserRestriction[]>(
    () => appUserInfo?.Restrictions || [],
    [appUserInfo?.Restrictions],
  );

  /**
   * Check if a module is enabled (NOT in restrictions with Type="Module").
   */
  const validateByModule = useCallback(
    (module: string): boolean => {
      // If there's no restrictions list, grant permission by default.
      if (!restrictions.length) {
        return true;
      }

      return !restrictions.some(
        (r) =>
          (r.Type || '').toLowerCase() === 'module' &&
          (r.Value || '').toLowerCase() === module.toLowerCase(),
      );
    },
    [restrictions],
  );

  /**
   * Check if a code is enabled (NOT in restrictions with Type="Code").
   */
  const validateByCode = useCallback(
    (code: string): boolean => {
      return !restrictions.some(
        (r) =>
          (r.Type || '').toLowerCase() === 'code' &&
          (r.Value || '').toLowerCase() === code.toLowerCase(),
      );
    },
    [restrictions],
  );

  /**
   * Check if a feature is enabled (NOT in restrictions with Type="Feature").
   */
  const validateByFeature = useCallback(
    (feature: string): boolean => {
      return !restrictions.some(
        (r) =>
          (r.Type || '').toLowerCase() === 'feature' &&
          (r.Value || '').toLowerCase() === feature.toLowerCase(),
      );
    },
    [restrictions],
  );

  /**
   * Check if a specific module key is enabled.
   * Alias for validateByModule for semantic clarity.
   */
  const isModuleEnabled = useCallback(
    (moduleKey: string): boolean => {
      return validateByModule(moduleKey);
    },
    [validateByModule],
  );

  /**
   * Checks if a module action is fully implemented and ready for use.
   */
  const isModuleImplemented = (module: ModuleDefinition): boolean => {
    switch (module.actionType) {
      case 'modal':
        return !!module.modalComponent;
      case 'sso':
        return !!module.ssoPath;
      case 'link':
        return !!module.linkUrl;
      case 'custom':
        // Custom actions need explicit implementation
        return false;
      default:
        return false;
    }
  };

  /**
   * Filter a module definitions object to return only enabled modules.
   */
  const getEnabledModules = useCallback(
    <T extends Record<string, ModuleDefinition>>(
      definitions: T,
    ): T[keyof T][] => {
      // We need to tell TypeScript that Object.values(definitions) are T[keyof T] (they all conform to ModuleDefinition)
      return (Object.values(definitions) as T[keyof T][]).filter((def) =>
        validateByModule(def.moduleKey),
      );
    },
    [validateByModule],
  );

  return {
    validateByModule,
    validateByCode,
    validateByFeature,
    isModuleEnabled,
    isModuleImplemented,
    getEnabledModules,
    restrictions,
  };
};

export default useModuleAccess;
